import {token} from "@atlaskit/tokens";
import type {ListFunnelProgress, ListFunnelSegmentState} from "~/components/ottopilotDataViewer/listFunnel";

const FUNNEL_SIZE_PX = 44;
const FUNNEL_VIEWBOX = 44;
const FUNNEL_CENTER = FUNNEL_VIEWBOX / 2;
const FUNNEL_OUTER_RADIUS = 19;
const FUNNEL_INNER_RADIUS = 14;
const SEGMENT_GAP_DEGREES = 3;
const SEGMENT_STROKE_WIDTH_PX = 0.538;

function segmentFillColor(state: ListFunnelSegmentState, hitColor?: string): string {
    switch (state) {
        case "hit":
            return hitColor ?? token("color.chart.green.bold");
        case "skipped":
            return token("color.chart.red.bold");
        case "pending":
            return token("color.chart.gray.bold");
    }
}

function segmentWedgePath(startDegrees: number, endDegrees: number): string {
    const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
    const pointOnCircle = (degrees: number, radius: number) => {
        const radians = toRadians(degrees);
        return {
            x: FUNNEL_CENTER + radius * Math.sin(radians),
            y: FUNNEL_CENTER - radius * Math.cos(radians),
        };
    };
    const outerStart = pointOnCircle(startDegrees, FUNNEL_OUTER_RADIUS);
    const outerEnd = pointOnCircle(endDegrees, FUNNEL_OUTER_RADIUS);
    const innerEnd = pointOnCircle(endDegrees, FUNNEL_INNER_RADIUS);
    const innerStart = pointOnCircle(startDegrees, FUNNEL_INNER_RADIUS);
    const sweepDegrees = endDegrees - startDegrees;
    const largeArcFlag = sweepDegrees > 180 ? 1 : 0;
    return [
        `M ${outerStart.x} ${outerStart.y}`,
        `A ${FUNNEL_OUTER_RADIUS} ${FUNNEL_OUTER_RADIUS} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}`,
        `L ${innerEnd.x} ${innerEnd.y}`,
        `A ${FUNNEL_INNER_RADIUS} ${FUNNEL_INNER_RADIUS} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}`,
        "Z",
    ].join(" ");
}

export function CircularListFunnel({
    progress,
    hitSegmentColor,
}: {
    progress: ListFunnelProgress;
    /** Override filled-segment color (e.g. Amaron disposition tracker teal). */
    hitSegmentColor?: string;
}) {
    const segmentCount = progress.segmentStates.length;
    if (segmentCount === 0) {
        return null;
    }

    const segmentSpanDegrees = (360 - SEGMENT_GAP_DEGREES * segmentCount) / segmentCount;

    return (
        <div
            className="relative grid shrink-0 place-items-center"
            style={{width: FUNNEL_SIZE_PX, height: FUNNEL_SIZE_PX}}
            aria-label={`${progress.filledCount} of ${progress.total} events with customer entry`}
        >
            <svg
                width={FUNNEL_SIZE_PX}
                height={FUNNEL_SIZE_PX}
                viewBox={`0 0 ${FUNNEL_VIEWBOX} ${FUNNEL_VIEWBOX}`}
                role="img"
                aria-hidden={true}
            >
                {Array.from({length: segmentCount}, (_, segmentIndex) => {
                    const startDegrees = segmentIndex * (segmentSpanDegrees + SEGMENT_GAP_DEGREES);
                    const endDegrees = startDegrees + segmentSpanDegrees;
                    const segmentState = progress.segmentStates[segmentIndex] ?? "pending";
                    return (
                        <path
                            key={segmentIndex}
                            d={segmentWedgePath(startDegrees, endDegrees)}
                            fill={segmentFillColor(segmentState, hitSegmentColor)}
                            stroke="#FFF"
                            strokeWidth={SEGMENT_STROKE_WIDTH_PX}
                        />
                    );
                })}
            </svg>
            <span
                className="pointer-events-none absolute"
                style={{
                    color: token("color.text.subtle"),
                    fontFamily: "Inter",
                    fontSize: "11px",
                    fontWeight: 500,
                    lineHeight: "14px",
                }}
            >
                {progress.label ?? `${progress.filledCount}/${progress.total}`}
            </span>
        </div>
    );
}
